import { useState, useEffect, useCallback } from "react"

interface ScrollDirectionOptions {
  /**
   * Threshold in pixels before considering scroll direction change
   */
  threshold?: number
  /**
   * Delay in ms before updating scroll direction
   */
  delay?: number
  /**
   * An optional offset (in pixels) where the element originally sits.
   * If provided, the hide effect only occurs if scrolling down past this offset.
   */
  triggerOffset?: number
}

/**
 * Custom hook to detect scroll direction with debouncing.
 * If a triggerOffset is provided, the element is only hidden when the
 * scroll position goes past that offset.
 * @param options Configuration options for scroll detection
 * @returns Object containing scroll direction state and visibility state
 */
export const useScrollDirection = ({ 
  threshold = 100, 
  delay = 50,
  triggerOffset
}: ScrollDirectionOptions = {}) => {
  const [scrollDirection, setScrollDirection] = useState<"up" | "down">("up")
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  // Use triggerOffset if provided, else fall back to threshold.
  const effectiveThreshold = triggerOffset !== undefined ? triggerOffset : threshold

  const updateScrollDirection = useCallback(() => {
    const currentScrollY = window.scrollY

    // Always show if the scroll position is above the effective threshold
    if (currentScrollY < effectiveThreshold) {
      setIsVisible(true)
      setScrollDirection("up")
      setLastScrollY(currentScrollY)
      return
    }

    // If scrolling down past the trigger point, hide the element
    if (currentScrollY > lastScrollY) {
      setScrollDirection("down")
      setIsVisible(false)
    } else if (currentScrollY < lastScrollY) {
      setScrollDirection("up")
      setIsVisible(true)
    }

    setLastScrollY(currentScrollY)
  }, [lastScrollY, effectiveThreshold])

  useEffect(() => {
    let rafId: number

    const handleScroll = () => {
      rafId = window.requestAnimationFrame(() => {
        updateScrollDirection()
      })
    }

    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      window.removeEventListener("scroll", handleScroll)
      if (rafId) {
        window.cancelAnimationFrame(rafId)
      }
    }
  }, [updateScrollDirection])

  return { scrollDirection, isVisible }
}
